import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, X } from 'lucide-react'
import { get, patch } from '@/lib/api'
import { useSocketEvent } from '@/lib/socket'

interface Alert {
  id: string
  alertType: string
  severity: string
  title: string
  message: string
  suggestedAction: string | null
  actionUrl: string | null
  entityType: string
  entityId: string
  status: string
  createdAt: string
}
interface Stats { unread: number; bySeverity: Record<string, number>; total: number }

const SEV: Record<string, { dot: string; label: string }> = {
  critical: { dot: '#EF4444', label: 'CRIT' },
  high: { dot: '#EF4444', label: 'HIGH' },
  medium: { dot: '#F59E0B', label: 'MED' },
  low: { dot: '#10B981', label: 'LOW' },
}

export function AIAlertCenter({ dark }: { dark?: boolean } = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: stats } = useQuery({ queryKey: ['ai-alert-stats'], queryFn: () => get<Stats>('/api/v1/ai/alerts/stats'), refetchInterval: 120_000 })
  const { data: alerts } = useQuery({ queryKey: ['ai-alerts'], queryFn: () => get<Alert[]>('/api/v1/ai/alerts', { query: { limit: 30 } }), enabled: open })

  // Realtime push — bump the badge + (if granted) a desktop notification.
  useSocketEvent('ai:alert', (e) => {
    qc.invalidateQueries({ queryKey: ['ai-alert-stats'] })
    qc.invalidateQueries({ queryKey: ['ai-alerts'] })
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(e.title, { body: e.message, icon: '/favicon.ico', tag: e.id })
      }
    } catch { /* notifications unavailable */ }
  })

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => undefined)
  }, [])
  useEffect(() => {
    if (!open) return
    const onClick = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['ai-alerts'] }); qc.invalidateQueries({ queryKey: ['ai-alert-stats'] }) }
  const read = useMutation({ mutationFn: (id: string) => patch(`/api/v1/ai/alerts/${id}/read`), onSuccess: invalidate })
  const dismiss = useMutation({ mutationFn: (id: string) => patch(`/api/v1/ai/alerts/${id}/dismiss`), onSuccess: invalidate })
  const acted = useMutation({ mutationFn: (id: string) => patch(`/api/v1/ai/alerts/${id}/acted`), onSuccess: invalidate })

  const unread = stats?.unread ?? 0
  const visible = (alerts ?? []).filter((a) => a.status !== 'dismissed')

  function markAll() {
    for (const a of visible) if (a.status === 'unread') read.mutate(a.id)
  }
  function view(a: Alert) {
    if (a.status === 'unread') read.mutate(a.id)
    if (a.actionUrl) { setOpen(false); navigate(a.actionUrl) }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative grid h-9 w-9 place-items-center rounded-lg ${dark ? 'text-white/80 hover:bg-white/10' : 'text-[#4B5563] hover:bg-[#F0ECFF]'}`}
        aria-label={t('ai.alerts', 'AI Alerts')}
      >
        <Sparkles className="h-[18px] w-[18px]" />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 min-w-4 h-4 px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold grid place-items-center">{unread > 99 ? '99+' : unread}</span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[380px] max-h-[70vh] overflow-y-auto rounded-xl bg-white border border-[#EEEDF5] shadow-[0_4px_16px_rgba(91,79,207,0.10)]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#EEEDF5] sticky top-0 bg-white">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1F1D35]"><Sparkles className="h-4 w-4 text-[#5B4FCF]" /> {t('ai.alerts', 'AI Alerts')} {unread > 0 ? <span className="text-[#9CA3AF] font-normal">({unread} {t('ai.unread', 'unread')})</span> : null}</span>
            {visible.some((a) => a.status === 'unread') ? <button onClick={markAll} className="text-xs font-semibold text-[#5B4FCF]">{t('ai.markAll', 'Mark all')}</button> : null}
          </div>

          {visible.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#9CA3AF]">{t('ai.noAlerts', 'No alerts. The AI engine will flag risks and opportunities here.')}</div>
          ) : (
            <ul className="divide-y divide-[#EEEDF5]">
              {visible.map((a) => {
                const sev = SEV[a.severity] ?? SEV.low
                return (
                  <li key={a.id} className={`px-4 py-3 ${a.status === 'unread' ? 'bg-[#FbFaFF]' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: sev!.dot }} />
                      <span className="text-[11px] font-bold tracking-wide" style={{ color: sev!.dot }}>{sev!.label}</span>
                      <span className="text-sm font-semibold text-[#1F1D35] truncate">{a.title}</span>
                    </div>
                    <p className="text-xs text-[#4B5563] mt-1">{a.message}</p>
                    {a.suggestedAction ? <p className="text-xs text-[#5B4FCF] mt-1">→ {a.suggestedAction}</p> : null}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex gap-2">
                        {a.actionUrl ? <button onClick={() => view(a)} className="text-xs font-semibold text-white bg-[#10B981] hover:bg-[#0EA572] rounded-lg px-2.5 py-1">{t('ai.view', 'View')}</button> : null}
                        <button onClick={() => acted.mutate(a.id)} className="text-xs font-semibold text-[#5B4FCF] bg-[#F0ECFF] rounded-lg px-2.5 py-1">{t('ai.actedOn', 'Acted')}</button>
                        <button onClick={() => dismiss.mutate(a.id)} className="text-xs text-[#9CA3AF] hover:text-[#991B1B] inline-flex items-center gap-0.5"><X className="h-3 w-3" /> {t('ai.dismiss', 'Dismiss')}</button>
                      </div>
                      <span className="text-[11px] text-[#9CA3AF]">{relativeTime(a.createdAt)}</span>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

function relativeTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}
